// frontend/src/pages/crm/VerifyEmail.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../utils/crmApi';
import { useCRM } from '../../contexts/CRMContext';
import toast from 'react-hot-toast';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useCRM();
    const token = searchParams.get('token');

    const [step, setStep] = useState('verifying'); // verifying, set-password, success, error
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setStep('error');
            setError('No verification token provided');
            return;
        }

        // Check if token is valid
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            // Just verify the token exists, we'll set password in next step
            setStep('set-password');
        } catch (error) {
            setStep('error');
            setError('Invalid or expired verification token');
        }
    };

    const handleSetPassword = async (e) => {
        e.preventDefault();
        
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const response = await authAPI.verifyEmail({
                token,
                password
            });

            if (response.data.success) {
                setStep('success');
                toast.success('Email verified! Please log in with your new password.');
                
                // Redirect to login page instead of auto-logging in
                setTimeout(() => navigate('/login', { replace: true }), 1500);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to verify email');
            setStep('error');
        } finally {
            setLoading(false);
        }
    };

    if (step === 'verifying') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary/5 dark:from-primary/10 via-background to-purple-50 dark:to-purple-950/10 flex items-center justify-center p-4">
                <div className="max-w-md w-full text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-muted-foreground">Verifying your email...</p>
                </div>
            </div>
        );
    }

    if (step === 'error') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary/5 dark:from-primary/10 via-background to-purple-50 dark:to-purple-950/10 flex items-center justify-center p-4">
                <div className="max-w-md w-full">
                    <div className="bg-card rounded-2xl shadow-xl p-8 border border-input focus:ring-2 focus:ring-ring focus:border-ring text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-950/20 rounded-full mb-4">
                            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground mb-2">
                            Verification Failed
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            {error}
                        </p>
                        <button
                            onClick={() => navigate('../signup')}
                            className="inline-block bg-gradient-to-r from-primary to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-primary/90 hover:to-purple-700 dark:hover:to-purple-600"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary/5 dark:from-primary/10 via-background to-purple-50 dark:to-purple-950/10 flex items-center justify-center p-4">
                <div className="max-w-md w-full">
                    <div className="bg-card rounded-2xl shadow-xl p-8 border border-input focus:ring-2 focus:ring-ring focus:border-ring text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-950/20 rounded-full mb-4">
                            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground mb-2">
                            All Set!
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            Your email has been verified successfully. Redirecting to your dashboard...
                        </p>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Set password step
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-4 shadow-lg">
                        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                        Set Your Password
                    </h1>
                    <p className="text-muted-foreground">
                        Choose a secure password for your account
                    </p>
                </div>

                <div className="bg-card rounded-2xl shadow-xl p-8 border border-input focus:ring-2 focus:ring-ring focus:border-ring">
                    <form onSubmit={handleSetPassword} className="space-y-6">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                placeholder="••••••••"
                                minLength={8}
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Must be at least 8 characters
                            </p>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                placeholder="••••••••"
                                minLength={8}
                            />
                        </div>

                        {password && confirmPassword && password !== confirmPassword && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                                Passwords do not match
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        >
                            {loading ? 'Setting up your account...' : 'Complete Setup'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;





